import React, { useState, ChangeEvent, useEffect } from 'react';
import styles from './style.module.css';
import Typography from '../Typography/Typography';
import FlatButtons from '../FlatButton/FlatButton';
import { ModalClose, StoreCloudUpload } from '../../assets/svgs';
import { useCreateProductMutation } from '../../redux/services/productApi';
import AnimatedButton from '../AnimatedButton';
import { useDispatch } from 'react-redux';
import { addProduct } from '../../redux/slices/createProductSlice';
import { IProductDetails } from '../../redux/services/productApi/interface';

interface Product {
    id: string;
    name: string;
    description: string;
    category: string;
    image: string;
    quantity: number;
}

interface AddProductModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAddProduct: (product: Product) => void;
    productIndex: number | null;
    storeId: string;
    children?: React.ReactNode;
}

export const AddProductModal = ({
    isOpen,
    onClose,
    onAddProduct,
    storeId,
    children
}: AddProductModalProps): React.ReactElement | null => {
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [createProduct, { isLoading: isCreating }] = useCreateProductMutation();
    const dispatch = useDispatch();

    const [formData, setFormData] = useState({
        name: '',
        description: '',
        category: ['Clothing'],
        storePosition: 'A1',
        basePrice: 0,
        stock: 0,
        discount: 0,
        discountType: 'percentage' as 'percentage' | 'fixed',
        categoryDetails: {
            sizes: [] as string[],
            colors: [] as string[],
            gender: ['Unisex'] as string[]
        }
    });

    const sizeOptions = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

    const handleImageUpload = (event: ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setErrorMessage('Please upload a valid image file.');
                return;
            }

            if (file.size > 10 * 1024 * 1024) {
                setErrorMessage('Image file size must be less than 10MB.');
                return;
            }

            setSelectedImage(file);
            const reader = new FileReader();
            reader.onload = (e) => {
                setImagePreview(e.target?.result as string);
            };
            reader.readAsDataURL(file);
            setErrorMessage('');
        }
    };

    const handleInputChange = (field: string, value: string | number | string[]): void => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        if (errorMessage) setErrorMessage('');
    };

    const handleCategoryDetailChange = (field: string, value: any): void => {
        setFormData(prev => ({
            ...prev,
            categoryDetails: {
                ...prev.categoryDetails,
                [field]: value
            }
        }));
    };

    const handleSizeToggle = (size: string): void => {
        setFormData(prev => ({
            ...prev,
            categoryDetails: {
                ...prev.categoryDetails,
                sizes: prev.categoryDetails.sizes.includes(size)
                    ? prev.categoryDetails.sizes.filter(s => s !== size)
                    : [...prev.categoryDetails.sizes, size]
            }
        }));
    };

    const handleGenderChange = (gender: string): void => {
        handleCategoryDetailChange('gender', [gender]);
    };

    const handleClose = (): void => {
        setSelectedImage(null);
        setImagePreview(null);
        setErrorMessage('');
        setIsSubmitting(false);
        setFormData({
            name: '',
            description: '',
            category: ['Clothing'],
            storePosition: 'A1',
            basePrice: 0,
            stock: 0,
            discount: 0,
            discountType: 'percentage',
            categoryDetails: {
                sizes: [],
                colors: [],
                gender: ['Unisex']
            }
        });
        onClose();
    };
    const handleSubmit = async (): Promise<void> => {
        setErrorMessage('');
        setIsSubmitting(false);
        setTimeout(() => handleClose(), 100);
        // setIsSubmitting(true);

        // Validation
        if (!formData.name.trim()) {
            setErrorMessage('Product name is required.');
            setIsSubmitting(false);
            return;
        }

        if (!formData.description.trim()) {
            setErrorMessage('Product description is required.');
            setIsSubmitting(false);
            return;
        }

        if (formData.basePrice <= 0) {
            setErrorMessage('Price must be greater than 0.');
            setIsSubmitting(false);
            return;
        }

        if (formData.stock < 0) {
            setErrorMessage('Stock cannot be negative.');
            setIsSubmitting(false);
            return;
        }

        if (!storeId) {
            setErrorMessage('Store ID is missing. Please try again.');
            setIsSubmitting(false);
            return;
        }

        try {
            const apiFormData = new FormData();
            apiFormData.append('name', formData.name.trim());
            apiFormData.append('description', formData.description.trim());
            apiFormData.append('price', formData.basePrice.toString());
            apiFormData.append('stock', formData.stock.toString());
            apiFormData.append('store_id', storeId);

            if (formData.discount > 0) {
                apiFormData.append('discount', formData.discount.toString());
                apiFormData.append('discount_type', formData.discountType);
            }

            formData.category.forEach(cat => {
                apiFormData.append('category', cat);
            });

            if (formData.categoryDetails.sizes.length > 0) {
                formData.categoryDetails.sizes.forEach(size => {
                    apiFormData.append('size_options', size);
                });
            }

            if (formData.categoryDetails.gender.length > 0) {
                formData.categoryDetails.gender.forEach(gender => {
                    apiFormData.append('gender_options', gender);
                });
            }

            if (formData.categoryDetails.colors.length > 0) {
                formData.categoryDetails.colors.forEach(color => {
                    apiFormData.append('color_options', color);
                });
            }

            apiFormData.append('position_in_store', formData.storePosition);

            if (selectedImage) {
                apiFormData.append('image_file', selectedImage);
            }

            console.log('FormData contents:');
            Array.from(apiFormData.entries()).forEach(([key, value]) => {
                console.log(`${key}:`, value);
            });

            const response = await createProduct(apiFormData).unwrap();

            console.log('Product created successfully:', response);

            // Use the actual stock value from the API response
            const newProduct: Product = {
                id: response._id,
                name: response.name,
                description: response.description,
                category: response.category[0] || 'Clothing',
                image: response.image_url || imagePreview || '',
                quantity: response.stock, // This will now be the correct stock value
            };

            onAddProduct(newProduct);

            // Dispatch to Redux after successful creation
            const productForRedux: IProductDetails = {
                store_id: response.store_id,
                name: response.name,
                description: response.description,
                price: response.price,
                stock: response.stock,
                position_in_store: formData.storePosition,
                image_file: response.image_url || imagePreview || '',
                category: formData.category,
                size_options: formData.categoryDetails.sizes,
                gender_options: formData.categoryDetails.gender,
                color_options: formData.categoryDetails.colors,
                discount: formData.discount,
                discount_type: formData.discountType,
                quantity: response.stock,
            };
            dispatch(addProduct(productForRedux));

            handleClose();
        } catch (error: any) {
            console.error('Failed to create product:', error);
            setErrorMessage('Failed to create product. Please try again.');
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        return () => {
            if (imagePreview) {
                URL.revokeObjectURL(imagePreview);
            }
        };
    }, [imagePreview]);

    if (!isOpen) return null;

    return (
        <div className={styles.modalOverlay} onClick={handleClose}>
        <div className={styles.modalOverlay} onClick={handleClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <Typography
                        variant='heading5'
                        fontFamily='satoshi'
                        weight='bold'
                        className={styles.modalTitle}>
                        Add Product
                    </Typography>

                    <FlatButtons
                        onClick={handleClose}
                        className={styles.closeButton}
                    >
                        <img src={ModalClose} alt='Close icon' />
                    </FlatButtons>
                </div>

                <div className={styles.modalBody}>
                    {errorMessage && (
                        <div className={styles.errorContainer}>
                            <Typography
                                variant='bodyRegular'
                                fontFamily='satoshi'
                                weight='regular'
                                className={styles.errorText}>
                                {errorMessage}
                            </Typography>
                        </div>
                    )}

                    <div className={styles.imageSection}>
                        <label htmlFor='product-image-upload' className={styles.sectionLabel}>
                            <Typography
                                variant='heading5'
                                fontFamily='satoshi'
                                weight='bold'
                                className={styles.sectionLabelText}>
                                Product Image
                            </Typography>
                        </label>

                        {!imagePreview ? (
                            <div className={styles.uploadArea}>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    className={styles.fileInput}
                                    id="product-image-upload"
                                />
                                <label htmlFor="product-image-upload" className={styles.uploadLabel}>
                                    <div className={styles.uploadIcon}>
                                        <img src={StoreCloudUpload} alt='store upload icon' />
                                    </div>
                                    <Typography
                                        variant='bodyRegular'
                                        fontFamily='satoshi'
                                        weight='regular'
                                        className={styles.uploadText}>
                                        Drag and drop files here
                                    </Typography>
                                    <Typography
                                        variant='bodyRegular'
                                        fontFamily='satoshi'
                                        weight='regular'
                                        className={styles.uploadSubtext}>
                                        or
                                    </Typography>
                                    <AnimatedButton
                                        variant='primary'
                                        type="button"
                                        className={styles.browseButton}
                                    >
                                        <Typography
                                            variant='buttonText'
                                            fontFamily='satoshi'
                                            weight='regular'
                                            size='base'
                                            className={styles.browseButtonText}>
                                            Browse
                                        </Typography>
                                    </AnimatedButton>
                                    <Typography
                                        variant='bodyRegular'
                                        fontFamily='satoshi'
                                        weight='regular'
                                        className={styles.supportedFormats}>
                                        Supported: JPG, PNG, WebP (Max 10MB)
                                    </Typography>
                                </label>
                            </div>
                        ) : (
                            <div className={styles.imagePreview}>
                                <img
                                    src={imagePreview}
                                    alt="Product preview"
                                    className={styles.previewImage}
                                />
                                <button
                                    type="button"
                                    onClick={() => {
                                        setImagePreview(null);
                                        setSelectedImage(null);
                                    }}
                                    className={styles.removeImageBtn}
                                >
                                    Change Image
                                </button>
                            </div>
                        )}

                        <Typography
                            variant='bodyRegular'
                            fontFamily='satoshi'
                            weight='regular'
                            className={styles.convertText}>
                            Don't have a 3D Model of your product?
                            <span
                                onClick={() => window.location.href = '/convert-image'}
                                className={styles.convertLink}
                            > Convert Image here
                            </span>
                        </Typography>
                    </div>

                    <div className={styles.formSection}>
                        <div className={styles.formRow}>
                            <div className={styles.formGroup1}>
                                <label htmlFor='name' className={styles.formLabel}>
                                    <Typography
                                        variant='heading4'
                                        fontFamily='satoshi'
                                        weight='bold'
                                        size='base'
                                        className={styles.labelText}>
                                        General Information
                                    </Typography>
                                </label>
                                <div className={styles.inputGroup}>
                                    <label htmlFor='name' className={styles.inputLabel}>
                                        <Typography
                                            variant='bodyRegular'
                                            fontFamily='satoshi'
                                            weight='regular'
                                            size='base'
                                            className={styles.labelText}>
                                            Product Name *
                                        </Typography>
                                    </label>
                                    <input
                                        type="text"
                                        id='name'
                                        placeholder="Highland Mid Calf Boots"
                                        value={formData.name}
                                        onChange={(e) => handleInputChange('name', e.target.value)}
                                        className={`${styles.formInput} ${errorMessage && !formData.name.trim() ? styles.formInputError : ''}`}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label htmlFor='description' className={styles.inputLabel}>
                                        <Typography
                                            variant='bodyRegular'
                                            fontFamily='satoshi'
                                            weight='regular'
                                            size='base'
                                            className={styles.labelText}>
                                            Product Description *
                                        </Typography>
                                    </label>
                                    <textarea
                                        id='description'
                                        placeholder="Enter product description..."
                                        value={formData.description}
                                        onChange={(e) => handleInputChange('description', e.target.value)}
                                        className={`${styles.formTextarea} ${errorMessage && !formData.description.trim() ? styles.formInputError : ''}`}
                                        rows={4}
                                    />
                                </div>
                                <div className={styles.inputGroup}>
                                    <label htmlFor='storePosition' className={styles.inputLabel}>
                                        <Typography
                                            variant='bodyRegular'
                                            fontFamily='satoshi'
                                            weight='regular'
                                            size='base'
                                            className={styles.labelText}>
                                            Store Position
                                        </Typography>
                                    </label>
                                    <select
                                        title='store position'
                                        id='storePosition'
                                        value={formData.storePosition}
                                        onChange={(e) => handleInputChange('storePosition', e.target.value)}
                                        className={styles.formSelect}
                                    >
                                        <option value="A1">A1</option>
                                        <option value="A2">A2</option>
                                        <option value="B1">B1</option>
                                        <option value="B2">B2</option>
                                        <option value="C1">C1</option>
                                        <option value="C2">C2</option>
                                    </select>
                                </div>
                            </div>

                            <div className={styles.formGroup2}>
                                <label htmlFor='category' className={styles.formLabel2}>
                                    <Typography
                                        variant='heading4'
                                        fontFamily='satoshi'
                                        weight='bold'
                                        size='base'
                                        className={styles.labelText}>
                                        Category Information
                                    </Typography>
                                </label>
                                <Typography
                                    variant='bodyRegular'
                                    fontFamily='satoshi'
                                    weight='regular'
                                    className={styles.categoryHelp}>
                                    First Select Product category to get category details
                                </Typography>
                                <div className={styles.inputGroup2}>
                                    <label htmlFor='category' className={styles.inputLabel}>
                                        <Typography
                                            variant='bodyRegular'
                                            fontFamily='satoshi'
                                            weight='regular'
                                            size='base'
                                            className={styles.labelText}>
                                            Add Product Category
                                        </Typography>
                                    </label>
                                    <select
                                        title='category section'
                                        id='category'
                                        value={formData.category[0]}
                                        onChange={(e) => handleInputChange('category', [e.target.value])}
                                        className={styles.formSelect}
                                    >
                                        <option value="Clothing">Clothing</option>
                                        <option value="Electronics">Electronics</option>
                                        <option value="Home">Home & Garden</option>
                                        <option value="Beauty">Beauty & Personal Care</option>
                                        <option value="Sports">Sports & Outdoors</option>
                                        <option value="Books">Books & Media</option>
                                    </select>
                                </div>

                                <div className={styles.categoryDetailsSection}>
                                    <Typography
                                        variant='bodyRegular'
                                        fontFamily='satoshi'
                                        weight='bold'
                                        className={styles.categoryDetailsLabel}>
                                        Category details
                                    </Typography>

                                    <div className={styles.sizeSection}>
                                        <Typography
                                            variant='bodyRegular'
                                            fontFamily='satoshi'
                                            weight='regular'
                                            className={styles.attributeLabel}>
                                            Size
                                        </Typography>
                                        <Typography
                                            variant='bodyRegular'
                                            fontFamily='satoshi'
                                            weight='regular'
                                            className={styles.attributeSubtext}>
                                            Pick all available size
                                        </Typography>
                                        <div className={styles.sizeOptions}>
                                            {sizeOptions.map((size) => (
                                                <button
                                                    key={size}
                                                    type="button"
                                                    className={`${styles.sizeButton} ${formData.categoryDetails.sizes.includes(size)
                                                        ? styles.sizeButtonActive
                                                        : ''
                                                        }`}
                                                    onClick={() => handleSizeToggle(size)}
                                                >
                                                    {size}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div className={styles.genderSection}>
                                        <Typography
                                            variant='bodyRegular'
                                            fontFamily='satoshi'
                                            weight='regular'
                                            className={styles.attributeLabel}>
                                            Gender
                                        </Typography>
                                        <Typography
                                            variant='bodyRegular'
                                            fontFamily='satoshi'
                                            weight='regular'
                                            className={styles.attributeSubtext}>
                                            Pick available gender
                                        </Typography>
                                        <div className={styles.genderOptions}>
                                            {['Men', 'Women', 'Unisex'].map((gender) => (
                                                <label key={gender} className={styles.genderOption}>
                                                    <input
                                                        type="radio"
                                                        name="gender"
                                                        value={gender}
                                                        checked={formData.categoryDetails.gender[0] === gender}
                                                        onChange={(e) => handleGenderChange(e.target.value)}
                                                        className={styles.genderRadio}
                                                    />
                                                    <span className={styles.genderLabel}>{gender}</span>
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={styles.pricingSection}>
                            <div className={styles.pricingHeader}>
                                <Typography
                                    variant='heading4'
                                    fontFamily='satoshi'
                                    weight='bold'
                                    size='base'
                                    className={styles.labelText}>
                                    Base Pricing and Discount
                                </Typography>
                            </div>

                            <div className={styles.pricingRow}>
                                <div className={styles.inputGroup}>
                                    <label htmlFor='basePrice' className={styles.inputLabel}>
                                        <Typography
                                            variant='bodyRegular'
                                            fontFamily='satoshi'
                                            weight='regular'
                                            size='base'
                                            className={styles.labelText}>
                                            Base Price *
                                        </Typography>
                                    </label>
                                    <input
                                        type="number"
                                        id='basePrice'
                                        placeholder="0.00"
                                        value={formData.basePrice || ''}
                                        onChange={(e) => handleInputChange('basePrice', parseFloat(e.target.value) || 0)}
                                        className={`${styles.formInput} ${errorMessage && formData.basePrice <= 0 ? styles.formInputError : ''}`}
                                        min="0"
                                        step="0.01"
                                    />
                                </div>

                                <div className={styles.inputGroup}>
                                    <label htmlFor='stock' className={styles.inputLabel}>
                                        <Typography
                                            variant='bodyRegular'
                                            fontFamily='satoshi'
                                            weight='regular'
                                            size='base'
                                            className={styles.labelText}>
                                            Stock *
                                        </Typography>
                                    </label>
                                    <input
                                        type="number"
                                        id='stock'
                                        placeholder="0"
                                        value={formData.stock || ''}
                                        onChange={(e) => handleInputChange('stock', parseInt(e.target.value) || 0)}
                                        className={styles.formInput}
                                        min="0"
                                    />
                                </div>

                                <div className={styles.inputGroup}>
                                    <label htmlFor='discount' className={styles.inputLabel}>
                                        <Typography
                                            variant='bodyRegular'
                                            fontFamily='satoshi'
                                            weight='regular'
                                            size='base'
                                            className={styles.labelText}>
                                            Discount
                                        </Typography>
                                    </label>
                                    <input
                                        type="number"
                                        id='discount'
                                        placeholder="0"
                                        value={formData.discount || ''}
                                        onChange={(e) => handleInputChange('discount', parseFloat(e.target.value) || 0)}
                                        className={styles.formInput}
                                        min="0"
                                        step="0.01"
                                    />
                                </div>

                                <div className={styles.inputGroup}>
                                    <label htmlFor='discountType' className={styles.inputLabel}>
                                        <Typography
                                            variant='bodyRegular'
                                            fontFamily='satoshi'
                                            weight='regular'
                                            size='base'
                                            className={styles.labelText}>
                                            Discount Type
                                        </Typography>
                                    </label>
                                    <select
                                        title='discount type'
                                        id='discountType'
                                        value={formData.discountType}
                                        onChange={(e) => handleInputChange('discountType', e.target.value)}
                                        className={styles.formSelect}
                                    >
                                        <option value="percentage">Percentage</option>
                                        <option value="fixed">Fixed Amount</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div className={styles.modalFooter}>
                            <div className={styles.footerNote}>
                                <AnimatedButton
                                    variant='primary'
                                    className={styles.addButton}
                                    onClick={handleSubmit}
                                    disabled={isSubmitting || isCreating}
                                >
                                    <Typography
                                        variant='buttonText'
                                        fontFamily='satoshi'
                                        weight='regular'
                                        size='base'
                                        className={styles.storeNameText}>
                                        {isSubmitting || isCreating ? 'Creating...' : (children || 'Add Product')}
                                    </Typography>
                                </AnimatedButton>
                            </div>
                        </div>
                    </div>
                </div>

                {/* <div className={styles.modalFooter}>
                    <div className={styles.footerNote}>
                        <AnimatedButton
                            variant='primary'
                            className={styles.addButton}
                            onClick={handleSubmit}
                            disabled={isSubmitting || isCreating}
                        >
                            <Typography
                                variant='buttonText'
                                fontFamily='satoshi'
                                weight='regular'
                                size='base'
                                className={styles.storeNameText}>
                                {isSubmitting || isCreating ? 'Creating...' : (children || 'Add Product')}
                            </Typography>
                        </AnimatedButton>
                    </div>
                </div> */}
            </div>
        </div>
        </div>
    );
}